import React from "react";
import { Appointment, ServiceType } from "../../../types";
import { formatDateForDisplay } from "../../../utils/dateUtils";
import { ActionButton } from "../../shared";

interface BookingConfirmationProps {
  appointment: Appointment;
  onNewBooking: () => void;
  onViewAppointments?: () => void;
}

export const BookingConfirmation: React.FC<BookingConfirmationProps> = ({
  appointment,
  onNewBooking,
  onViewAppointments,
}) => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-green-50 to-teal-50 flex items-center justify-center p-4 sm:p-6">
      <div className="w-full max-w-4xl mx-auto animate-slide-up">
        {/* Success Animation */}
        <div className="text-center mb-6 sm:mb-8">
          <div className="relative inline-flex items-center justify-center">
            <div className="w-20 h-20 sm:w-24 sm:h-24 bg-gradient-to-br from-emerald-500 to-green-600 rounded-full flex items-center justify-center shadow-large animate-pulse-soft">
              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-white rounded-full flex items-center justify-center">
                <span className="text-3xl sm:text-4xl text-emerald-600 animate-bounce">
                  ✓
                </span>
              </div>
            </div>
            {/* Celebration particles */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              <div
                className="absolute -top-2 -left-2 w-6 h-6 text-yellow-400 animate-bounce"
                style={{ animationDelay: "0.1s" }}
              >
                ✨
              </div>
              <div
                className="absolute -top-1 -right-1 w-6 h-6 text-pink-400 animate-bounce"
                style={{ animationDelay: "0.3s" }}
              >
                🎉
              </div>
              <div
                className="absolute -bottom-1 -left-1 w-6 h-6 text-blue-400 animate-bounce"
                style={{ animationDelay: "0.5s" }}
              >
                💫
              </div>
              <div
                className="absolute -bottom-2 -right-2 w-6 h-6 text-purple-400 animate-bounce"
                style={{ animationDelay: "0.7s" }}
              >
                🌟
              </div>
            </div>
          </div>

          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 mt-4 sm:mt-6 mb-2 sm:mb-3 px-4">
            🎊 Agendamento Confirmado! 🎊
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-gray-600 font-medium px-4">
            Seu agendamento foi realizado com sucesso!
          </p>
        </div>

        {/* Appointment Details Card */}
        <div className="bg-white rounded-2xl sm:rounded-3xl shadow-large border border-gray-100 overflow-hidden mb-6 sm:mb-8">
          <div className="bg-gradient-to-r from-emerald-500 to-green-600 px-4 sm:px-6 md:px-8 py-4 sm:py-6">
            <h3 className="text-lg sm:text-xl font-bold text-white flex items-center gap-2 sm:gap-3">
              <span className="text-xl sm:text-2xl">📋</span>
              Detalhes do Agendamento
            </h3>
          </div>

          <div className="p-4 sm:p-6 md:p-8 space-y-4 sm:space-y-6">
            <div className="grid grid-cols-1 gap-4 sm:gap-6">
              <div className="flex items-center justify-between py-3 sm:py-4 border-b border-gray-100">
                <div className="flex items-center gap-2 sm:gap-3 md:gap-4 min-w-0 flex-1">
                  <div className="w-8 h-8 sm:w-9 sm:h-9 md:w-10 md:h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-blue-600 text-sm sm:text-base">📅</span>
                  </div>
                  <span className="font-semibold text-sm sm:text-base text-gray-700">Data:</span>
                </div>
                <span className="font-bold text-sm sm:text-base md:text-lg text-gray-800 text-right ml-2">
                  {formatDateForDisplay(appointment.appointmentDate)}
                </span>
              </div>

              {appointment.service?.type === ServiceType.TIME && (
                <div className="flex items-center justify-between py-3 sm:py-4 border-b border-gray-100">
                  <div className="flex items-center gap-2 sm:gap-3 md:gap-4 min-w-0 flex-1">
                    <div className="w-8 h-8 sm:w-9 sm:h-9 md:w-10 md:h-10 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-purple-600 text-sm sm:text-base">⏰</span>
                    </div>
                    <span className="font-semibold text-sm sm:text-base text-gray-700">
                      Horário:
                    </span>
                  </div>
                  <span className="font-bold text-sm sm:text-base md:text-lg text-gray-800 text-right ml-2 whitespace-nowrap">
                    {appointment.startTime && appointment.endTime
                      ? `${appointment.startTime} - ${appointment.endTime}`
                      : appointment.startTime || "Horário não especificado"}
                  </span>
                </div>
              )}
              <div className="flex items-center justify-between py-3 sm:py-4 border-b border-gray-100">
                <div className="flex items-center gap-2 sm:gap-3 md:gap-4 min-w-0 flex-1">
                  <div className="w-8 h-8 sm:w-9 sm:h-9 md:w-10 md:h-10 bg-pink-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-pink-600 text-sm sm:text-base">✂️</span>
                  </div>
                  <span className="font-semibold text-sm sm:text-base text-gray-700">Serviço:</span>
                </div>
                <span className="font-bold text-sm sm:text-base md:text-lg text-gray-800 text-right ml-2 truncate">
                  {appointment.service?.name}
                </span>
              </div>

              {appointment.service?.type === ServiceType.TIME && (
                <div className="flex items-center justify-between py-3 sm:py-4">
                  <div className="flex items-center gap-2 sm:gap-3 md:gap-4">
                    <div className="w-8 h-8 sm:w-9 sm:h-9 md:w-10 md:h-10 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-amber-600 text-sm sm:text-base">⏱️</span>
                    </div>
                    <span className="font-semibold text-sm sm:text-base text-gray-700">
                      Duração:
                    </span>
                  </div>
                  <span className="font-bold text-sm sm:text-base md:text-lg text-gray-800 text-right ml-2">
                    {appointment.service?.duration} minutos
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 mb-6 sm:mb-8">
          <ActionButton
            onClick={onNewBooking}
            variant="primary"
            size="lg"
            className="flex items-center justify-center gap-2 sm:gap-3"
          >
            <span className="text-lg sm:text-xl">📅</span>
            <span className="text-sm sm:text-base">Agendar Outro Horário</span>
          </ActionButton>

          {onViewAppointments && (
            <ActionButton
              onClick={onViewAppointments}
              variant="outline"
              size="lg"
              className="flex items-center justify-center gap-2 sm:gap-3"
            >
              <span className="text-sm sm:text-base">Ver Todos os Agendamentos</span>
            </ActionButton>
          )}
        </div>

        {/* Important Note */}
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 border-l-4 border-amber-400 p-4 sm:p-6 rounded-r-xl sm:rounded-r-2xl">
          <div className="flex items-start gap-3 sm:gap-4">
            <div className="flex-shrink-0">
              <span className="text-xl sm:text-2xl">💡</span>
            </div>
            <div>
              <h4 className="font-bold text-sm sm:text-base text-amber-800 mb-1 sm:mb-2">Importante:</h4>
              <p className="text-sm sm:text-base text-amber-700 leading-relaxed">
                Seu agendamento foi confirmado! Se precisar cancelar ou
                remarcar, entre em contato conosco com pelo menos{" "}
                <strong>24 horas de antecedência</strong>.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
